import { FeedbackStatus, FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { User } from "types/user";
import { REQUIREMENT } from "helpers/constants";

export type LearnerRequirementProgress = {
  requirementId: string;
  requirement: Requirement;
  /** DONE feedbacks that include this requirement for the learner. */
  attempts: number;
  minAttempts: number;
  /** Max coach_rating across DONE feedbacks; null when none. */
  currentScore: number | null;
  targetScore: number | null;
  meetsAttempts: boolean;
  meetsScore: boolean;
  /** All feedback requests (any status) involving this requirement. */
  feedbacks: FeedbackType[];
};

export type ProcessedLearner = {
  studentId: string;
  student: User;
  requirements: LearnerRequirementProgress[];
  onTrackCount: number;
  behindCount: number;
  totalFeedbacks: number;
};

export type LearnerPerformancePoint = {
  date: string;
  value: number;
  data: {
    feedbackId: string;
    learnerId: string;
  };
};

export type LearnerPerformanceSeries = {
  learnerId: string;
  name: string;
  data: LearnerPerformancePoint[];
};

const TRACKED_TYPES = new Set([
  REQUIREMENT.REQUIREMENT_EPA,
  REQUIREMENT.REQUIREMENT_COMPETENCY,
]);

export function isTrackedRequirementType(type?: string | null): boolean {
  return Boolean(type && TRACKED_TYPES.has(type as REQUIREMENT));
}

function feedbacksForRequirement(
  feedbacks: FeedbackType[],
  requirementId: string,
): FeedbackType[] {
  return feedbacks.filter((feedback) =>
    feedback.feedback_skills?.some(
      (skill) => skill.requirement?.requirement_id === requirementId,
    ),
  );
}

function doneAttempts(feedbacks: FeedbackType[]): number {
  return feedbacks.filter((feedback) => feedback.status === FeedbackStatus.DONE)
    .length;
}

function maxCoachRating(
  feedbacks: FeedbackType[],
  requirementId: string,
): number | null {
  const ratings = feedbacks
    .filter((feedback) => feedback.status === FeedbackStatus.DONE)
    .flatMap((feedback) =>
      (feedback.feedback_skills || [])
        .filter((skill) => skill.requirement?.requirement_id === requirementId)
        .map((skill) => skill.coach_rating)
        .filter((rating): rating is number => typeof rating === "number"),
    );
  if (ratings.length === 0) return null;
  return Math.max(...ratings);
}

export function buildLearnerRequirementProgress(
  requirement: Requirement,
  learnerFeedbacks: FeedbackType[],
): LearnerRequirementProgress | null {
  const requirementId = requirement.requirement_id;
  if (!requirementId || !isTrackedRequirementType(requirement.type)) {
    return null;
  }

  const related = feedbacksForRequirement(learnerFeedbacks, requirementId);
  const attempts = doneAttempts(related);
  const minAttempts = requirement.min_performance || 1;
  const targetScore =
    typeof requirement.min_score === "number" ? requirement.min_score : null;
  const currentScore = maxCoachRating(related, requirementId);
  const meetsAttempts = attempts >= minAttempts;
  const meetsScore =
    targetScore === null ||
    (currentScore !== null && currentScore >= targetScore);

  return {
    requirementId,
    requirement,
    attempts,
    minAttempts,
    currentScore,
    targetScore,
    meetsAttempts,
    meetsScore,
    feedbacks: related,
  };
}

export function processLearnersAnalytics(
  learners: User[],
  orgRequirements: Requirement[],
  feedbacks: FeedbackType[],
): ProcessedLearner[] {
  const trackedRequirements = orgRequirements.filter((requirement) =>
    isTrackedRequirementType(requirement.type),
  );

  const feedbacksByStudent = new Map<string, FeedbackType[]>();
  for (const feedback of feedbacks) {
    if (!feedback.student_id) continue;
    const list = feedbacksByStudent.get(feedback.student_id) || [];
    list.push(feedback);
    feedbacksByStudent.set(feedback.student_id, list);
  }

  // Include learners who only appear in feedbacks (no graduation-class row).
  const learnersById = new Map<string, User>();
  for (const learner of learners) {
    if (learner.user_id) learnersById.set(learner.user_id, learner);
  }
  for (const feedback of feedbacks) {
    if (feedback.student_id && feedback.student && !learnersById.has(feedback.student_id)) {
      learnersById.set(feedback.student_id, feedback.student);
    }
  }

  return [...learnersById.values()]
    .map((student) => {
      const studentId = student.user_id || "";
      const learnerFeedbacks = feedbacksByStudent.get(studentId) || [];
      const requirements = trackedRequirements
        .map((requirement) =>
          buildLearnerRequirementProgress(requirement, learnerFeedbacks),
        )
        .filter((row): row is LearnerRequirementProgress => Boolean(row))
        .sort((a, b) => a.requirement.name.localeCompare(b.requirement.name));

      const onTrackCount = requirements.filter(
        (row) => row.meetsAttempts && row.meetsScore,
      ).length;
      const behindCount = requirements.length - onTrackCount;

      return {
        studentId,
        student,
        requirements,
        onTrackCount,
        behindCount,
        totalFeedbacks: learnerFeedbacks.length,
      };
    })
    .sort((a, b) =>
      (a.student.name || a.student.email).localeCompare(
        b.student.name || b.student.email,
      ),
    );
}

/**
 * Builds one chronological coach-rating series per learner for a requirement.
 * Multiple matching skills in the same feedback are represented by their
 * average so each feedback request contributes exactly one chart point.
 */
export function buildLearnerPerformanceSeries(
  requirementId: string,
  learners: User[],
  feedbacks: FeedbackType[],
): LearnerPerformanceSeries[] {
  const learnersById = new Map(
    learners
      .filter((learner) => Boolean(learner.user_id))
      .map((learner) => [learner.user_id as string, learner]),
  );
  const pointsByLearner = new Map<string, LearnerPerformancePoint[]>();

  for (const feedback of feedbacks) {
    if (
      feedback.status !== FeedbackStatus.DONE ||
      !feedback.student_id
    ) {
      continue;
    }

    const ratings = (feedback.feedback_skills || [])
      .filter(
        (skill) =>
          skill.requirement?.requirement_id === requirementId &&
          typeof skill.coach_rating === "number",
      )
      .map((skill) => skill.coach_rating as number);
    if (ratings.length === 0) continue;

    const date =
      feedback.observation_date || feedback.reviewed_at || feedback.created_at;
    if (!date || Number.isNaN(new Date(date).getTime())) continue;

    if (feedback.student && !learnersById.has(feedback.student_id)) {
      learnersById.set(feedback.student_id, feedback.student);
    }

    const points = pointsByLearner.get(feedback.student_id) || [];
    points.push({
      date,
      value: ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length,
      data: {
        feedbackId: feedback.feedback_id,
        learnerId: feedback.student_id,
      },
    });
    pointsByLearner.set(feedback.student_id, points);
  }

  return [...pointsByLearner.entries()]
    .map(([learnerId, data]) => {
      const learner = learnersById.get(learnerId);
      return {
        learnerId,
        name: learner?.name || learner?.email || learnerId,
        data: data.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        ),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
